import React, { useState } from 'react';
import { View, TextInput, TouchableOpacity, NativeSyntheticEvent, TextInputKeyPressEventData, StyleProp, TextStyle, ViewStyle } from 'react-native';
import { translate } from '../services/translate';
import MyAppText from './MyAppText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';

type PasswordInputProps = {
    label: string;
    placeholder?: string;
    errorMessage?: string;
    disabled?: boolean;
    onKeyPress?: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
    labelFontSize?: number;
    required?: boolean;
    labelStyle?: StyleProp<TextStyle>;
    viewStyle?: StyleProp<ViewStyle>;
} & ({
    form?: never;
    fieldName?: never;
    customValidations?: never;
    pattern?: never;
    value: string;
    onChangeText: (value: string) => void;
    invalid: (value: string) => boolean;
} | {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    value?: never;
    onChangeText?: never;
    invalid?: never;
    customValidations?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (value: any) => string | true;
    };
    pattern?: {
        value: RegExp;
        message: string;
    };
});

export default function PasswordInput({ label, placeholder, errorMessage, value, onChangeText, invalid, disabled, onKeyPress, labelFontSize, required, labelStyle, viewStyle, form, fieldName, customValidations, pattern }: PasswordInputProps) {
    const [isDirty, setIsDirty] = useState(false);
    const [isInvalid, setIsInvalid] = useState(false);
    const [showContent, setShowContent] = useState(false);
    const { styles, theme } = useStyles(stylesheet);

    disabled = !!disabled;

    if (form && fieldName) {
        const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

        if (required) {
            rules.required = translate('required_field');
        }

        if (pattern) {
            rules.pattern = pattern;
        }

        if (customValidations) {
            rules.validate = { ...customValidations };
        }

        return <Controller
            control={form.control}
            name={fieldName}
            rules={rules}
            render={({ field, fieldState }) => (
                <View style={[{ rowGap: 5 }, viewStyle]}>
                    <View style={{ flexDirection: 'row', gap: 3 }}>
                        {label && <MyAppText style={labelStyle ? labelStyle : styles.label(labelFontSize ?? 16)}>{label}</MyAppText>}
                        {required ?
                            <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                            : null}
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <TextInput
                            onBlur={field.onBlur}
                            autoComplete='password-new'
                            editable={!disabled}
                            placeholder={placeholder ? placeholder : label}
                            style={[styles.input, {
                                color: !field.value ? theme.colors.placeholder : theme.colors.textColor
                            }, disabled ? styles.disabled : (isInvalid ? styles.invalid : null)]}
                            value={field.value}
                            onChangeText={(value) => field.onChange(value)}
                            secureTextEntry={showContent ? false : true}
                            onKeyPress={onKeyPress}
                        />
                        <TouchableOpacity onPress={() => setShowContent(!showContent)} style={[styles.eyeBox, isInvalid ? styles.invalid : null]} delayPressIn={100}>
                            <FontAwesomeIcon icon={showContent ? faEyeSlash : faEye} color={theme.colors.iconColor} fontSize={16}></FontAwesomeIcon>
                        </TouchableOpacity>
                    </View>
                    {isInvalid ?
                        <MyAppText style={styles.error}>{errorMessage ? errorMessage : translate('required_field')}</MyAppText> : null}
                    {fieldState.error ? <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText> : null}
                </View>
            )}
        />;

    } else if (value !== undefined && onChangeText !== undefined && invalid !== undefined) {
        return (
            <View style={[{ rowGap: 5 }, viewStyle]}>
                <View style={{ flexDirection: 'row', gap: 3 }}>
                    {label && <MyAppText style={labelStyle ? labelStyle : styles.label(labelFontSize ?? 16)}>{label}</MyAppText>}
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                </View>
                <View style={{ flexDirection: 'row' }}>
                    <TextInput
                        autoComplete='password-new'
                        editable={!disabled}
                        placeholder={placeholder ? placeholder : label}
                        style={[styles.input, {
                            color: value == '' ? theme.colors.placeholder : theme.colors.textColor
                        }, disabled ? styles.disabled : (isInvalid ? styles.invalid : null)]}
                        value={value}
                        onBlur={() => {
                            setIsInvalid(isDirty && invalid(value));
                        }}
                        onChangeText={(value) => {
                            if (!invalid(value)) {
                                setIsInvalid(false);
                            }
                            setIsDirty(true);
                            onChangeText(value);
                        }}
                        secureTextEntry={showContent ? false : true}
                        onKeyPress={onKeyPress}
                    />
                    <TouchableOpacity onPress={() => setShowContent(!showContent)} style={[styles.eyeBox, isInvalid ? styles.invalid : null]} delayPressIn={100}>
                        <FontAwesomeIcon icon={showContent ? faEyeSlash : faEye} color={theme.colors.iconColor} fontSize={16}></FontAwesomeIcon>
                    </TouchableOpacity>
                </View>
                {isInvalid ?
                    <MyAppText style={styles.error}>{errorMessage ? errorMessage : translate('required_field')}</MyAppText> : null}
            </View>
        );
    }
}

const stylesheet = createStyleSheet(theme => ({
    input: {
        flexGrow: 1,
        fontSize: 16,
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.field.background,
        borderColor: theme.colors.field.border,
        borderWidth: 1,
        height: 40,
        padding: 10,
        borderRadius: 4,
        borderEndEndRadius: 0,
        borderTopEndRadius: 0,
    },
    eyeBox: {
        width: 40,
        height: 40,
        justifyContent: 'center',
        alignItems: 'center',
        borderColor: theme.colors.neutral['300'],
        borderWidth: 1,
        borderRadius: 4,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        borderLeftWidth: 0
    },
    error: {
        color: '#FF0000',
        fontSize: 10
    },
    invalid: {
        borderColor: '#FF0000',
    },
    disabled: {
        backgroundColor: theme.colors.field.disabledBackground,
    },
    label: (fontSize: number) => ({
        color: theme.colors.labelColor,
        fontSize,
        height: 25,
        fontWeight: '400'
    }),
}));
